/**
 * La ligne de séance telle qu'elle atteint **ma tête de lecture**.
 *
 * # Le délai, et ce qu'il ne couvrait pas
 *
 * La tête court `D` derrière la position de séance : c'est le délai de
 * restitution, celui du jam, et celui — agrandi de son horizon — du spectateur
 * d'un récital. Tant que la séance défile, rien d'autre n'est nécessaire.
 *
 * Une pause, elle, s'appliquait à l'instant où l'état publié arrivait : le
 * transport se figeait à la position de la pause, et la tête `D` derrière.
 * Chez le spectateur, quatre secondes avant la dernière note de l'interprète,
 * qui ne sonnaient qu'à la reprise. Et une reprise ou un déplacement sautaient
 * de même `D` trop tôt.
 *
 * # La règle
 *
 * Chaque changement de ligne prend effet chez moi `D` après l'instant où il a
 * eu lieu, quand ma tête l'atteint. Pendant une pause, la position tenue est
 * celle de la pause plus `D`, pour que la tête s'arrête **sur** la pause. En
 * marche, rien ne change : la position tenue est la ligne publiée elle-même, et
 * la tête `D` derrière elle.
 *
 * C'est la même règle pour tout le monde, jam compris, avec le délai de chacun.
 * Le leader, dont le délai vaut zéro, applique tout à l'instant, comme avant.
 *
 * Les commandes, elles, partent toujours sur la ligne publiée : c'est elle que
 * le service arbitre.
 */

/** Ce qui définit la ligne dans un état publié. */
export interface LinePoint {
  positionMs: number;
  /** Instant serveur du changement, en millisecondes. */
  atMs: number;
  paused: boolean;
}

/** Ce que le transport partagé doit tenir à un instant. */
export interface DelayedLineReading {
  /** Position de séance à tenir, en millisecondes. */
  positionMs: number;
  paused: boolean;
  /** Instant serveur du prochain changement à appliquer, ou `null`. */
  nextChangeAtMs: number | null;
}

/** Au-delà, les plus anciens changements tombent : un seul reste utile une fois atteint. */
const MAX_POINTS = 32;

export class DelayedLine {
  private points: LinePoint[] = [];

  /** Retient un état publié. Un état qui ne change pas la ligne n'ajoute rien. */
  push(point: LinePoint): void {
    const last = this.points[this.points.length - 1];

    if (
      last !== undefined
      && last.positionMs === point.positionMs
      && last.atMs === point.atMs
      && last.paused === point.paused
    ) {
      return;
    }

    this.points.push({ ...point });
    this.points.sort((a, b) => a.atMs - b.atMs);

    if (this.points.length > MAX_POINTS) {
      this.points.splice(0, this.points.length - MAX_POINTS);
    }
  }

  reset(): void {
    this.points = [];
  }

  get isEmpty(): boolean {
    return this.points.length === 0;
  }

  /**
   * La ligne à tenir à l'instant serveur `nowMs`, pour un délai `delayMs`.
   *
   * Un arrivant ne connaît que l'état courant, même s'il a changé il y a moins
   * de `D` : il l'applique tout de suite, faute de savoir ce qui précédait.
   */
  read(nowMs: number, delayMs: number): DelayedLineReading | null {
    if (this.points.length === 0) {
      return null;
    }

    const delay = Math.max(0, delayMs);
    let active = 0;

    for (let i = 1; i < this.points.length; i++) {
      if (this.points[i].atMs + delay <= nowMs) {
        active = i;
      }
    }

    // Ce qui précède le changement en vigueur ne servira plus.
    if (active > 0) {
      this.points.splice(0, active);
    }

    const point = this.points[0];
    const next = this.points[1];

    return {
      positionMs: point.paused ? point.positionMs + delay : point.positionMs + (nowMs - point.atMs),
      paused: point.paused,
      nextChangeAtMs: next === undefined ? null : next.atMs + delay,
    };
  }
}
